
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppBreadcrumbs } from "@/components/Layout/AppBreadcrumbs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { PublishDialog } from "./PublishDialog";
import { Settings, LayoutTemplate, Save, ArrowLeft, Sparkles } from "lucide-react";
import { FunnelTemplates } from "./FunnelTemplates";
import { SaveTemplateDialog } from "./components/SaveTemplateDialog";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/auth";
import type { FunnelData } from "./types";

interface BuilderHeaderProps {
  funnelData: FunnelData;
  onLoadTemplate: (template: FunnelData) => void;
  isPro?: boolean;
  isUpgrading?: boolean;
  isSaving?: boolean;
  isPublished?: boolean;
  onUpgrade?: () => Promise<void>;
  onPublish?: () => void;
  onSave?: () => Promise<void>;
}

export const BuilderHeader = ({ 
  funnelData, 
  onLoadTemplate, 
  isPro = false,
  isUpgrading = false,
  isSaving = false,
  isPublished = false,
  onUpgrade,
  onPublish,
  onSave
}: BuilderHeaderProps) => {
  const [showPublish, setShowPublish] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const { user } = useAuth();
  const isSuperUser = isSuperAdmin(user);

  const buttonClasses = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent";

  return (
    <div className="border-b space-y-4">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className={buttonClasses}
          >
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </Button>
          <AppBreadcrumbs />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTemplates(true)}
            className={buttonClasses}
          >
            <LayoutTemplate className="w-4 h-4" />
            Templates
          </Button>
          {isSuperUser && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSaveTemplate(true)}
              className={buttonClasses}
            >
              <Save className="w-4 h-4" />
              Save as Template
            </Button>
          )}
          {onSave && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSave}
              disabled={isSaving}
              className={buttonClasses}
            >
              <Save className="w-4 h-4" />
              {isSaving ? "Saving..." : "Save"}
            </Button>
          )}
          {isPro ? (
            <Button
              variant="default"
              size="sm"
              disabled
              className={`${buttonClasses} bg-purple-100 text-purple-800 disabled:opacity-50`}
            >
              <Sparkles className="w-4 h-4" />
              Pro
            </Button>
          ) : onUpgrade && (
            <Button
              variant="default"
              size="sm"
              onClick={onUpgrade}
              disabled={isUpgrading}
              className={`${buttonClasses} bg-purple-100 text-purple-800 hover:bg-purple-200 disabled:opacity-50`}
            >
              <Sparkles className="w-4 h-4" />
              {isUpgrading ? "Upgrading..." : "Upgrade to Pro"}
            </Button>
          )}
          {!isPublished && onPublish && (
            <Button
              variant="outline"
              size="sm"
              onClick={onPublish}
              className={`${buttonClasses} bg-[#a1cae1] text-white hover:bg-[#8bb8d3]`}
            >
              Publish
            </Button>
          )}
          {isPublished && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPublish(true)}
              className={buttonClasses}
            >
              <Settings className="w-4 h-4" />
              Settings
            </Button>
          )}
        </div>
      </div>

      <PublishDialog
        isOpen={showPublish}
        onOpenChange={setShowPublish}
        funnelId={funnelData.id || ""}
        isPublished={isPublished}
        currentSubdomain={funnelData.subdomain}
        isPro={isPro}
        onUpdate={(data) => {
          // Handle update if needed
        }}
      />

      <FunnelTemplates
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSelect={onLoadTemplate}
        isPro={isPro}
      />

      {isSuperUser && (
        <SaveTemplateDialog
          isOpen={showSaveTemplate}
          onOpenChange={setShowSaveTemplate}
          funnelData={funnelData}
          mode="funnel"
        />
      )}
    </div>
  );
};
